import { useState, useEffect, useCallback } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useClientAuth } from '../../context/ClientAuthContext'
import { useClientSync } from '../../hooks/useClientSync'
import {
  LayoutDashboard, Bot, CreditCard, FileText, LogOut,
  AlertTriangle, BarChart3, Menu, X, Sun, Moon, Monitor,
  PanelLeftOpen, PanelLeftClose, Receipt
} from 'lucide-react'
// Use the same Layout.css as main dashboard for identical styling
import '../Layout.css'

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { path: '/bots', icon: Bot, label: 'My Bots' },
  { path: '/stats', icon: BarChart3, label: 'Statistics' },
  { path: '/billing', icon: CreditCard, label: 'Billing' },
  { path: '/invoices', icon: Receipt, label: 'Invoices' },
  { path: '/requests', icon: FileText, label: 'Requests' }
]

export default function ClientLayout() {
  const { client, logout, refreshClient, token, setClient } = useClientAuth()
  const navigate = useNavigate()

  // Refresh client data (balance, etc.) periodically
  useEffect(() => {
    refreshClient()
    const interval = setInterval(refreshClient, 60000)
    return () => clearInterval(interval)
  }, [])

  // SSE Sync Handlers
  const handleBalanceUpdate = useCallback((data) => {
    setClient(prev => prev ? { ...prev, balance: data.balance_after } : prev)
  }, [setClient])

  const handleGracePeriodUpdate = useCallback((data) => {
    setClient(prev => prev ? {
      ...prev,
      grace_period_active: data.grace_period_active,
      admin_extension_days: data.extension_days
    } : prev)
  }, [setClient])

  // SSE Connection for real-time updates
  const { connected: sseConnected, reconnecting } = useClientSync({
    token,
    onBalanceUpdate: handleBalanceUpdate,
    onGracePeriodUpdate: handleGracePeriodUpdate,
    enabled: !!token
  })

  // Sidebar state - starts closed
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Theme state: 'light', 'dark', or 'auto'
  const [themeMode, setThemeMode] = useState(() => {
    const isAuto = localStorage.getItem('redstone-theme-auto') !== 'false'
    if (isAuto) return 'auto'
    return localStorage.getItem('redstone-theme') || 'dark'
  })

  // Track actual applied theme (for logo selection)
  const [appliedTheme, setAppliedTheme] = useState('dark')

  useEffect(() => {
    const updateAppliedTheme = () => {
      const theme = document.documentElement.getAttribute('data-theme') || 'dark'
      setAppliedTheme(theme)
    }
    updateAppliedTheme()
    // Watch for theme changes
    const observer = new MutationObserver(updateAppliedTheme)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    return () => observer.disconnect()
  }, [])

  // Update theme when themeMode changes
  useEffect(() => {
    if (window.themeUtils) {
      if (themeMode === 'auto') {
        window.themeUtils.setAutoTheme()
      } else {
        window.themeUtils.setTheme(themeMode)
      }
    }
  }, [themeMode])

  const cycleTheme = () => {
    // Cycle: auto -> light -> dark -> auto
    if (themeMode === 'auto') setThemeMode('light')
    else if (themeMode === 'light') setThemeMode('dark')
    else setThemeMode('auto')
  }

  const getThemeIcon = () => {
    if (themeMode === 'auto') return Monitor
    if (themeMode === 'light') return Sun
    return Moon
  }

  const getThemeLabel = () => {
    if (themeMode === 'auto') return 'Auto'
    if (themeMode === 'light') return 'Light'
    return 'Dark'
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const getInitials = (name) => {
    if (!name) return '?'
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  const formatBalance = (balance) => {
    const num = parseFloat(balance) || 0
    return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
  }

  const showGraceBanner = client?.balance <= 0 || !!client?.grace_period_active

  const sidebarVariants = {
    open: { width: 280, transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] } },
    closed: { width: 80, transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] } }
  }

  const labelVariants = {
    open: { opacity: 1, x: 0, display: 'block', transition: { delay: 0.1 } },
    closed: { opacity: 0, x: -10, transitionEnd: { display: 'none' } }
  }

  return (
    <div className="app-layout">
      {/* Mobile Menu Button */}
      <button
        className="mobile-menu-btn"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
      >
        {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar - using same classes as main Layout */}
      <motion.aside
        className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}
        variants={sidebarVariants}
        animate={sidebarOpen ? 'open' : 'closed'}
        initial="closed"
        style={{ width: sidebarOpen ? 280 : 80 }}
      >
        {/* Logo */}
        <div className="sidebar-header">
          <motion.div
            className="logo"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/')}
          >
            <div className="logo-with-status">
              <img
                src={appliedTheme === 'light' ? '/logo-light.png' : '/logo-dark.png'}
                alt="Redstone"
                className="logo-img"
              />
              <span
                className={`sync-dot ${sseConnected ? 'connected' : reconnecting ? 'reconnecting' : 'disconnected'}`}
                title={sseConnected ? 'Live sync active' : reconnecting ? 'Reconnecting...' : 'Offline'}
              />
            </div>
            <AnimatePresence>
              {sidebarOpen && (
                <motion.span
                  className="logo-text"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  Redstone AI
                </motion.span>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Toggle button - inline when open */}
          {sidebarOpen && (
            <motion.button
              className="collapse-btn"
              onClick={() => setSidebarOpen(false)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title="Collapse sidebar"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <PanelLeftClose size={20} />
            </motion.button>
          )}
        </div>

        {/* Toggle button - below header when closed */}
        {!sidebarOpen && (
          <motion.div
            className="sidebar-toggle-collapsed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <motion.button
              className="collapse-btn centered"
              onClick={() => setSidebarOpen(true)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title="Expand sidebar"
            >
              <PanelLeftOpen size={20} />
            </motion.button>
          </motion.div>
        )}

        {/* Navigation */}
        <nav className="sidebar-nav">
          {navItems.map((item, index) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.exact}
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
              title={!sidebarOpen ? item.label : undefined}
            >
              {({ isActive }) => (
                <motion.div
                  className="nav-item-content"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ x: 4 }}
                >
                  {isActive && (
                    <motion.div
                      className="nav-indicator"
                      layoutId="nav-indicator"
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    />
                  )}
                  <item.icon size={20} className="nav-icon" />
                  <motion.span
                    className="nav-label"
                    variants={labelVariants}
                  >
                    {item.label}
                  </motion.span>
                </motion.div>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="sidebar-footer">
          <div className="user-info">
            <motion.div
              className="user-avatar"
              whileHover={{ scale: 1.05 }}
            >
              {getInitials(client?.name)}
            </motion.div>
            <motion.div
              className="user-details"
              variants={labelVariants}
            >
              <span className="user-name">{client?.name || 'Client'}</span>
              <span className={`user-role ${parseFloat(client?.balance) <= 0 ? 'text-danger' : 'text-success'}`}>
                {formatBalance(client?.balance)}
              </span>
            </motion.div>
          </div>

          {/* Theme Toggle */}
          <motion.button
            className="theme-toggle-btn"
            onClick={cycleTheme}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            title={!sidebarOpen ? `Theme: ${getThemeLabel()}` : undefined}
          >
            {(() => {
              const ThemeIcon = getThemeIcon()
              return <ThemeIcon size={20} />
            })()}
            <motion.span variants={labelVariants}>{getThemeLabel()}</motion.span>
          </motion.button>

          <motion.button
            className="logout-btn"
            onClick={handleLogout}
            whileHover={{ scale: 1.02, backgroundColor: 'rgba(239, 68, 68, 0.15)' }}
            whileTap={{ scale: 0.98 }}
            title={!sidebarOpen ? 'Sign Out' : undefined}
          >
            <LogOut size={20} />
            <motion.span variants={labelVariants}>Sign Out</motion.span>
          </motion.button>
        </div>
      </motion.aside>

      {/* Mobile Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="mobile-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Main Content */}
      <motion.main
        className="main-content"
        initial={{ marginLeft: 80 }}
        animate={{
          marginLeft: sidebarOpen ? 280 : 80
        }}
        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      >
        <div className="page-container">
          {showGraceBanner && (
            <GracePeriodBanner
              balance={client?.balance}
              graceActive={client?.grace_period_active}
              graceStarted={client?.grace_period_started}
              extensionDays={client?.admin_extension_days}
            />
          )}
          <Outlet />
        </div>
      </motion.main>
    </div>
  )
}

function GracePeriodBanner({ balance, graceActive, graceStarted, extensionDays }) {
  const { clientApi } = useClientAuth()
  const isUrgent = graceActive

  // Calculate days remaining in grace period
  const getDaysRemaining = () => {
    if (!graceActive || !graceStarted) return null
    const graceDuration = (extensionDays || 0) + 7 // Default 7 days + any admin extension
    const startDate = new Date(graceStarted)
    const endDate = new Date(startDate.getTime() + graceDuration * 24 * 60 * 60 * 1000)
    const now = new Date()
    const daysLeft = Math.ceil((endDate - now) / (24 * 60 * 60 * 1000))
    return Math.max(0, daysLeft)
  }

  const daysRemaining = getDaysRemaining()

  const handleActivateGrace = async () => {
    try {
      await clientApi('/grace-period', { method: 'POST' })
      window.location.reload()
    } catch (error) {
      console.error('Failed to activate grace period:', error)
    }
  }

  return (
    <div className={`grace-banner ${isUrgent ? 'urgent' : ''}`} style={{
      padding: 'var(--space-md) var(--space-lg)',
      background: isUrgent
        ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.1) 100%)'
        : 'linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(239, 68, 68, 0.08) 100%)',
      border: `1px solid ${isUrgent ? 'rgba(239, 68, 68, 0.3)' : 'rgba(245, 158, 11, 0.25)'}`,
      borderRadius: 'var(--radius-lg)',
      marginBottom: 'var(--space-lg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 'var(--space-md)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
        <div style={{
          width: '40px',
          height: '40px',
          background: isUrgent ? 'rgba(220, 38, 38, 0.15)' : 'rgba(245, 158, 11, 0.15)',
          borderRadius: 'var(--radius-md)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: isUrgent ? 'var(--danger)' : 'var(--warning)',
          flexShrink: 0
        }}>
          <AlertTriangle size={24} />
        </div>
        <div>
          <h3 style={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: isUrgent ? 'var(--danger)' : 'var(--warning)',
            marginBottom: '2px'
          }}>
            {graceActive
              ? `Grace Period Active${daysRemaining !== null ? ` - ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining` : ''}`
              : 'Low Balance Warning'}
          </h3>
          <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', margin: 0 }}>
            {graceActive
              ? 'Your bots will be suspended when the grace period expires. Please add funds to continue.'
              : `Your account balance is $${parseFloat(balance).toFixed(2)}. Add funds to avoid service interruption.`
            }
          </p>
        </div>
      </div>

      {!graceActive && parseFloat(balance) <= 0 && (
        <button
          onClick={handleActivateGrace}
          style={{
            padding: 'var(--space-sm) var(--space-md)',
            background: 'var(--warning)',
            color: '#0f172a',
            fontSize: '0.875rem',
            fontWeight: 600,
            border: 'none',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          Activate Grace Period
        </button>
      )}
    </div>
  )
}
